import { useState, useRef, useEffect } from 'react';
import { Text, Box, Icon } from '@nimbus-ds/components';
import { CheckCircleIcon, CloseIcon } from '@nimbus-ds/icons';
import AiCreditsBadge from 'commons/components/AiCreditsBadge';
import { FeedbackButtons } from 'commons/components/FeedbackButtons';
import { useTranslationCommon } from 'commons/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';

type Feedback = 'positive' | 'negative';

interface ActionableToastProps {
  message: string;
  thumbsUpAriaLabel?: string;
  thumbsDownAriaLabel?: string;
  onPositiveFeedback: () => void;
  onNegativeFeedback: () => void;
  remainingAiUses?: number;
  onClose?: () => void;
}

function ActionableToast({
  message,
  thumbsUpAriaLabel,
  thumbsDownAriaLabel,
  onPositiveFeedback,
  onNegativeFeedback,
  remainingAiUses,
  onClose,
}: Readonly<ActionableToastProps>): JSX.Element {
  const [feedbackGiven, setFeedbackGiven] = useState<Feedback | null>(null);
  const t = useTranslationCommon();
  const tCatalog = useTranslationCatalog();
  const timeoutRef = useRef<number>(0);

  const handleFeedback = (feedback: 'positive' | 'negative') => {
    if (feedback === 'positive') {
      onPositiveFeedback();
      setFeedbackGiven('positive');
    } else {
      onNegativeFeedback();
      setFeedbackGiven('negative');
    }
  };

  useEffect(() => {
    if (feedbackGiven && onClose) {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => {
        onClose();
      }, 2000);
    }

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [feedbackGiven, onClose]);

  return (
    <Box
      width="100%"
      paddingX="2"
      paddingY="1"
      borderColor="neutral-surfaceHighlight"
      borderWidth="1"
      borderStyle="solid"
      borderRadius="2"
      position="relative"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="2"
      >
        <Box display="flex" alignItems="center" gap="2">
          <Icon
            source={<CheckCircleIcon size="small" />}
            color="success-interactive"
          />
          <Text fontSize="caption" color="neutral-textLow">
            {feedbackGiven
              ? tCatalog('products.detail.aiGenerated.feedbackMessage')
              : message}
          </Text>
          <FeedbackButtons
            feedbackGiven={feedbackGiven}
            onPositiveFeedback={() => handleFeedback('positive')}
            onNegativeFeedback={() => handleFeedback('negative')}
            thumbsUpAriaLabel={
              thumbsUpAriaLabel || t('aiFeedbackModal.thumbsUpAriaLabel')
            }
            thumbsDownAriaLabel={
              thumbsDownAriaLabel || t('aiFeedbackModal.thumbsDownAriaLabel')
            }
          />
        </Box>
        <Box display="flex" alignItems="center" gap="2">
          {remainingAiUses !== undefined && (
            <AiCreditsBadge remainingAiUses={remainingAiUses} />
          )}
          {onClose && (
            <Box
              as="button"
              type="button"
              onClick={onClose}
              aria-label={t('aiGeneration.resultsModal.cta.close')}
              backgroundColor="transparent"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              p="1"
            >
              <CloseIcon size="small" />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default ActionableToast;
